import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Sparkles, ClipboardEdit, Wand2, ListChecks, Plus, Trash2, RefreshCw, ArrowRight, BookOpen } from "lucide-react";

const BLANK = { description: "", target_title: "", target_chapters: 12, target_pages: 200, mode: "self", chapters: [] };

export default function OutlinePanel({ bookId, book, onChaptersGenerated }) {
  const [outline, setOutline] = useState(BLANK);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [review, setReview] = useState(null);
  const [genBusy, setGenBusy] = useState(false);
  const [step, setStep] = useState("mode");  // mode | brief | edit

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/books/${bookId}/outline`);
        const o = { ...BLANK, ...(data.outline || {}) };
        setOutline(o);
        if (o.chapters?.length) setStep("edit");
      } catch {
        toast.error("Failed to load outline");
      } finally {
        setLoading(false);
      }
    })();
  }, [bookId]);

  const persist = async (next) => {
    setSaving(true);
    try {
      const { data } = await api.put(`/books/${bookId}/outline`, next);
      setOutline(data.outline);
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const setField = (k, v) => setOutline((o) => ({ ...o, [k]: v }));

  const updateChapter = (idx, k, v) => {
    setOutline((o) => {
      const chapters = o.chapters.slice();
      chapters[idx] = { ...chapters[idx], [k]: v };
      return { ...o, chapters };
    });
  };

  const addChapter = () => {
    setOutline((o) => ({
      ...o,
      chapters: [...(o.chapters || []), { title: `Chapter ${(o.chapters?.length || 0) + 1}`, summary: "" }],
    }));
  };

  const removeChapter = (idx) => {
    setOutline((o) => ({ ...o, chapters: o.chapters.filter((_, i) => i !== idx) }));
  };

  const suggest = async () => {
    setAiBusy(true);
    try {
      const { data } = await api.post(`/books/${bookId}/outline/suggest`, {
        description: outline.description,
        target_title: outline.target_title,
        target_chapters: outline.target_chapters,
        target_pages: outline.target_pages,
      });
      const next = {
        ...outline,
        mode: "ai",
        target_title: data.target_title || outline.target_title,
        chapters: data.chapters || [],
      };
      setOutline(next);
      await persist(next);
      setStep("edit");
      toast.success("Outline suggested");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "AI suggestion failed");
    } finally {
      setAiBusy(false);
    }
  };

  const requestReview = async () => {
    setAiBusy(true);
    setReview(null);
    try {
      const { data } = await api.post(`/books/${bookId}/outline/review`, { outline });
      setReview(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Review failed");
    } finally {
      setAiBusy(false);
    }
  };

  const applyRevised = async () => {
    if (!review?.revised_chapters?.length) return;
    const next = { ...outline, chapters: review.revised_chapters.map((c) => ({ title: c.title, summary: c.summary })) };
    setOutline(next);
    await persist(next);
    setReview(null);
    toast.success("Applied AI revisions");
  };

  const generateChapters = async () => {
    if (!outline.chapters?.length) {
      toast.error("Add some outline chapters first");
      return;
    }
    if (!confirm(`Create ${outline.chapters.length} empty chapters in your book from this outline?`)) return;
    setGenBusy(true);
    try {
      // make sure latest outline is persisted
      await persist(outline);
      const { data } = await api.post(`/books/${bookId}/outline/generate-chapters`);
      toast.success(`${data.created} chapter${data.created === 1 ? "" : "s"} created — head to the Write tab`);
      onChaptersGenerated && onChaptersGenerated(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setGenBusy(false);
    }
  };

  if (loading) return <p className="text-sm" style={{ color: "var(--bf-text-muted)" }} data-testid="outline-loading">Loading…</p>;

  return (
    <div className="max-w-4xl space-y-8" data-testid="outline-panel">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-serif text-3xl mb-1">Outline</h2>
          <p className="text-sm" style={{ color: "var(--bf-text-muted)" }}>
            Plan the book before writing. Everything here can still be tweaked from the chapter editor later.
          </p>
        </div>
        <div className="flex gap-2">
          {outline.chapters?.length > 0 && (
            <Button
              onClick={generateChapters}
              disabled={genBusy}
              className="text-white"
              style={{ background: "var(--bf-primary)" }}
              data-testid="outline-generate-chapters-btn"
            >
              {genBusy ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <ArrowRight className="w-4 h-4 mr-2" />}
              Generate {outline.chapters.length} chapters
            </Button>
          )}
        </div>
      </div>

      {/* Mode picker (shown if no outline yet, or always at the top) */}
      <section className="grid sm:grid-cols-3 gap-3" data-testid="outline-mode-cards">
        {[
          { id: "self", label: "Plan it myself", desc: "Free-form chapter list you build by hand.", Icon: ClipboardEdit },
          { id: "ai", label: "Suggest with AI", desc: "AI proposes a full outline from your brief.", Icon: Wand2 },
          { id: "review", label: "AI review my outline", desc: "Paste/write your outline, get feedback + revisions.", Icon: ListChecks },
        ].map(({ id, label, desc, Icon }) => (
          <button
            key={id}
            onClick={() => { setField("mode", id); setStep(id === "self" ? "edit" : "brief"); }}
            className="text-left p-4 rounded-lg transition-all hover:-translate-y-0.5"
            style={{
              background: outline.mode === id ? "var(--bf-surface)" : "white",
              border: outline.mode === id ? "2px solid var(--bf-primary)" : "1px solid var(--bf-border)",
            }}
            data-testid={`outline-mode-${id}`}
          >
            <Icon className="w-5 h-5 mb-2" style={{ color: "var(--bf-primary)" }} />
            <p className="font-medium text-sm">{label}</p>
            <p className="text-xs mt-1" style={{ color: "var(--bf-text-muted)" }}>{desc}</p>
          </button>
        ))}
      </section>

      {/* Brief inputs (always visible — used by self+ai+review) */}
      <section className="grid sm:grid-cols-2 gap-4" data-testid="outline-brief">
        <div className="sm:col-span-2">
          <label className="label-eyebrow block mb-1.5">Brief description / premise</label>
          <Textarea
            rows={3}
            value={outline.description}
            onChange={(e) => setField("description", e.target.value)}
            placeholder="A retired detective takes one last case in the village she grew up in…"
            data-testid="outline-description"
          />
        </div>
        <div>
          <label className="label-eyebrow block mb-1.5">Expected title</label>
          <Input
            value={outline.target_title}
            onChange={(e) => setField("target_title", e.target.value)}
            placeholder="Working title"
            data-testid="outline-target-title"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label-eyebrow block mb-1.5">Chapters</label>
            <Input
              type="number"
              min={1}
              max={80}
              value={outline.target_chapters || ""}
              onChange={(e) => setField("target_chapters", Number(e.target.value))}
              data-testid="outline-target-chapters"
            />
          </div>
          <div>
            <label className="label-eyebrow block mb-1.5">Pages (approx.)</label>
            <Input
              type="number"
              min={1}
              max={2000}
              value={outline.target_pages || ""}
              onChange={(e) => setField("target_pages", Number(e.target.value))}
              data-testid="outline-target-pages"
            />
          </div>
        </div>
        <div className="sm:col-span-2 flex flex-wrap gap-2">
          {outline.mode === "ai" && (
            <Button
              onClick={suggest}
              disabled={aiBusy}
              className="text-white"
              style={{ background: "var(--bf-primary)" }}
              data-testid="outline-ai-suggest-btn"
            >
              {aiBusy ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
              {aiBusy ? "Thinking…" : "Suggest outline"}
            </Button>
          )}
          {outline.mode === "review" && outline.chapters?.length > 0 && (
            <Button
              onClick={requestReview}
              disabled={aiBusy}
              className="text-white"
              style={{ background: "var(--bf-primary)" }}
              data-testid="outline-ai-review-btn"
            >
              {aiBusy ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
              {aiBusy ? "Reviewing…" : "Review my outline"}
            </Button>
          )}
          <Button
            variant="outline"
            onClick={() => persist(outline)}
            disabled={saving}
            data-testid="outline-save-btn"
          >
            {saving ? "Saving…" : "Save brief"}
          </Button>
        </div>
      </section>

      {/* Review feedback */}
      {review && (
        <section className="p-5 rounded-lg" style={{ background: "var(--bf-surface)", border: "1px solid var(--bf-border)" }} data-testid="outline-review-result">
          <div className="flex justify-between items-start gap-3 mb-2">
            <div>
              <p className="label-eyebrow mb-1">AI feedback</p>
              <p className="font-serif text-lg" data-testid="outline-review-verdict">{review.verdict}</p>
            </div>
            <Badge style={{ background: "var(--bf-primary)", color: "white" }}>{review.score}/100</Badge>
          </div>
          <ul className="space-y-1.5 mt-3 text-sm">
            {(review.improvements || []).map((imp, i) => (
              <li key={imp} className="flex gap-2"><span style={{ color: "var(--bf-primary)" }}>→</span><span>{imp}</span></li>
            ))}
          </ul>
          {review.revised_chapters?.length > 0 && (
            <Button onClick={applyRevised} className="mt-4 text-white" style={{ background: "var(--bf-primary)" }} data-testid="outline-apply-revised-btn">
              Apply AI revisions ({review.revised_chapters.length} chapters)
            </Button>
          )}
        </section>
      )}

      {/* Editable chapter list */}
      <section data-testid="outline-chapter-list">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-serif text-2xl flex items-center gap-2"><BookOpen className="w-5 h-5" /> Chapters ({outline.chapters?.length || 0})</h3>
          <Button variant="outline" size="sm" onClick={addChapter} data-testid="outline-add-chapter-btn">
            <Plus className="w-4 h-4 mr-1.5" /> Add chapter
          </Button>
        </div>
        {!outline.chapters?.length && (
          <p className="text-sm py-6 text-center" style={{ color: "var(--bf-text-muted)" }} data-testid="outline-empty">
            No chapters yet. Pick a mode above to begin, or click &quot;Add chapter&quot;.
          </p>
        )}
        <ul className="space-y-3">
          {(outline.chapters || []).map((c, idx) => (
            <li
              key={c.chapter_id || `oc-${idx}`}
              className="p-4 rounded-lg flex gap-3"
              style={{ background: "white", border: "1px solid var(--bf-border)" }}
              data-testid={`outline-chapter-${idx}`}
            >
              <div className="font-serif text-xl flex-shrink-0 w-8" style={{ color: "var(--bf-text-muted)" }}>{idx + 1}</div>
              <div className="flex-1 space-y-2 min-w-0">
                <Input
                  value={c.title}
                  onChange={(e) => updateChapter(idx, "title", e.target.value)}
                  placeholder="Chapter title"
                  className="font-serif text-base"
                  data-testid={`outline-chapter-title-${idx}`}
                />
                <Textarea
                  rows={2}
                  value={c.summary}
                  onChange={(e) => updateChapter(idx, "summary", e.target.value)}
                  placeholder="What happens in this chapter?"
                  data-testid={`outline-chapter-summary-${idx}`}
                />
              </div>
              <Button variant="ghost" size="sm" onClick={() => removeChapter(idx)} data-testid={`outline-chapter-remove-${idx}`}>
                <Trash2 className="w-4 h-4" />
              </Button>
            </li>
          ))}
        </ul>
        {outline.chapters?.length > 0 && (
          <div className="mt-4 flex gap-2">
            <Button onClick={() => persist(outline)} disabled={saving} variant="outline" data-testid="outline-save-chapters-btn">
              {saving ? "Saving…" : "Save outline"}
            </Button>
          </div>
        )}
      </section>
    </div>
  );
}
